import { X } from "lucide-react"
import { useEffect, useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FilterForm } from "./filter-form"
import { ColumnFilterValue, FilterTypeId } from "@/types/editor"

interface FilterPillProps {
  filterId: string
  columnName: string
  filterLabel: string
  filterValue: ColumnFilterValue | undefined
  allKeys: string[]
  columnNames: Record<string, string>
  onRemove: (id: string) => void
  onUpdate: (col: string, type: FilterTypeId, value: string) => void
}

export function FilterPill({
  filterId,
  columnName,
  filterLabel,
  filterValue,
  allKeys,
  columnNames,
  onRemove,
  onUpdate,
}: FilterPillProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [editCol, setEditCol] = useState(filterId)
  const [editType, setEditType] = useState<FilterTypeId>(filterValue?.type ?? "contains")
  const [editValue, setEditValue] = useState(filterValue?.value ?? "")

  useEffect(() => {
    if (isOpen) {
      setEditCol(filterId)
      setEditType(filterValue?.type ?? "contains")
      setEditValue(filterValue?.value ?? "")
    }
  }, [isOpen, filterId, filterValue])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50/80 px-1.5 py-0.5 text-[10px] text-blue-800 dark:border-blue-800 dark:bg-blue-950/60 dark:text-blue-200"
        >
          <span className="max-w-[100px] truncate">{columnName}</span>
          <span className="text-blue-600 dark:text-blue-400">· {filterLabel}</span>
          <X
            className="h-3 w-3 shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              onRemove(filterId)
            }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" className="w-56 p-2">
        <FilterForm
          columnId={editCol}
          filterType={editType}
          filterValue={editValue}
          allKeys={allKeys}
          columnNames={columnNames}
          onColumnChange={setEditCol}
          onFilterTypeChange={setEditType}
          onFilterValueChange={setEditValue}
          onApply={(col, type, val) => {
            onUpdate(col, type, val)
            setIsOpen(false)
          }}
          onCancel={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}
